import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CommonChars } from '@sb-shared/globals/common-chars';
import { formElements } from './../../constants/shared.constant';

@Component({
    selector: 'sb-telephone-input',
    templateUrl: './telephone-input.component.html',
    styleUrls: ['./telephone-input.component.scss'],
    standalone: false
})
export class TelephoneInputComponent {

  @Input() model: any;
  @Output() modelChange = new EventEmitter<void>();
  @Input() parentId: string;
  @Input() dialCountry: any;
  @Input() fieldId: string;
  @Input() isRequired: boolean;
  @Input() isDisabled: boolean;
  @Input() form: UntypedFormGroup;

  // Variables
  phoneCountryCode: string = '';
  countries: Array<any>;
  internationalPrefix: string = "+";
  formElements: any;

  constructor() {
    this.formElements = formElements;
  }

  // Receives the country list once sb-select has loaded it, then initialises from the current value
  setOptions(options) {
    if (Array.isArray(options) && options.length > 0) {
      this.countries = options;
      this.model = this.model || '';
      this.initialise();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // The country list isn't ready yet - setOptions handles the first initialisation
    if (!this.countries?.length) {
      return;
    }
    // Re-initialise (without altering the number) when either input changes externally
    if (changes.model || changes.dialCountry) {
      this.initialise();
    }
  }

  // Work out the dial country/code from the current number, falling back to the supplied country.
  // Never modifies the number itself.
  private initialise() {
    const detected = this.detectCountryFromNumber(this.model);
    if (detected) {
      this.dialCountry = detected.id;
      this.phoneCountryCode = detected.phoneCountryCode;
      return;
    }
    // No international prefix on the number - keep the provided/default country
    if (!this.dialCountry && this.countries?.length) {
      this.dialCountry = this.countries[0].id;
    }
    this.phoneCountryCode = this.getCountryById(this.dialCountry)?.phoneCountryCode || '';
  }

  // Finds the country a prefixed number belongs to, preferring the longest matching dial code
  // (e.g. 1242 over 1) and then the default for shared codes (e.g. GB over Isle of Man for 44).
  private detectCountryFromNumber(number: any) {
    if (!number || !this.countries || number.substring(0, 1) !== this.internationalPrefix) {
      return null;
    }
    const digits = number.replaceAll(this.internationalPrefix, CommonChars.Blank);
    const matched = this.countries.filter(country => digits.indexOf(country.phoneCountryCode) === 0);
    if (matched.length === 0) {
      return null;
    }
    const longestCodeLength = Math.max(...matched.map(country => country.phoneCountryCode.length));
    const longestMatches = matched.filter(country => country.phoneCountryCode.length === longestCodeLength);
    return longestMatches.find(country => country.isPhoneDefault) || longestMatches[0];
  }

  private getCountryById(id: any) {
    return this.countries?.find(country => country.id === id);
  }

  onDialCountrySelectChanged($event) {
    // User picked a different country - swap the dial code on the number
    this.dialCountry = $event.value;
    const country = this.getCountryById(this.dialCountry);
    if (!country) {
      return;
    }
    const newCode = country.phoneCountryCode;
    const hasPrefix = this.model && this.model.substring(0, 1) === this.internationalPrefix;
    if (hasPrefix && this.phoneCountryCode) {
      // Replace just the existing dial code, keeping the rest of the number
      const existingPrefix = this.internationalPrefix + this.phoneCountryCode;
      this.model = this.internationalPrefix + newCode + this.model.substring(existingPrefix.length);
    } else {
      // Nothing usable to keep - start a fresh prefix
      this.model = this.internationalPrefix + newCode;
    }
    this.phoneCountryCode = newCode;
    this.emitModel();
  }

  disableDialCountrySelect() {
    return this.model && this.model.substring(0, 1) !== this.internationalPrefix;
  }

  onChangeNumber() {
    // User edited the number - re-detect the country from any prefix, then emit
    const detected = this.detectCountryFromNumber(this.model);
    if (detected) {
      this.dialCountry = detected.id;
      this.phoneCountryCode = detected.phoneCountryCode;
    }
    this.emitModel();
  }

  private emitModel() {
    let model = this.model;
    // A bare dial code with no actual number means 'no value'
    if (model === this.internationalPrefix + this.phoneCountryCode) {
      model = null;
    }
    this.modelChange.emit(model);
  }
}
